import { queryOptions } from '@tanstack/react-query'

import { knockOutPublicFetcher } from '@/libs/fetcher/presets'

import type { PredictionContractGroup, PredictionContractsResponse } from './types'

/**
 * 批量合约端点（cdc-web `fetchEventContractsBatched` 的 RQ 版）：
 * `/public/api/v1/contracts?event_id=id1,id2,…`，单次最多 20 个 ID
 * （cdc-web `MAX_IDS_PER_CHUNK`），超出切片并行请求后 flat。
 *
 * 列表端点（/api/v1/events）不返回合约——卡片的头像/价格全靠这里二次
 * enrich：league → `event.contracts` 顶层，match → `event.sections`
 * （sections 形状已在 PredictionContractGroup 上，由卡片 hook 消费）。
 */

const MAX_IDS_PER_CHUNK = 20

const fetchEventContracts = async (ids: string[]): Promise<PredictionContractGroup[]> => {
  if (ids.length === 0) return []
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += MAX_IDS_PER_CHUNK) {
    chunks.push(ids.slice(i, i + MAX_IDS_PER_CHUNK))
  }
  const results = await Promise.all(
    chunks.map(chunkIds =>
      // successResponseDataProperty: '' 保留 {has_more, data} 原样（同 events.ts）。
      knockOutPublicFetcher<PredictionContractsResponse>(
        '/api/v1/contracts',
        { queries: { event_id: chunkIds.join(',') } },
        { successResponseDataProperty: '' },
      ).then(res => res?.data ?? []),
    ),
  )
  return results.flat()
}

export function predictionEventContractsQueryOptions(eventIds: string[]) {
  // key 用排序后的稳定 ID 串：同一批事件顺序抖动不触发 refetch。fetch 也走
  // 这份排序后的 ids（key 与 queryFn 闭包同源），enrichment 对合约顺序不敏感。
  const sortedIds = [...eventIds].sort((a, b) => a.localeCompare(b))
  return queryOptions({
    queryKey: ['prediction', 'event-contracts', sortedIds] as const,
    queryFn: () => fetchEventContracts(sortedIds),
    staleTime: 15 * 1000,
  })
}
